/**
 * Historique des séances — 3ᵉ sous-onglet de **Musculation**
 * (`ARCHITECTURE_V2.md` §4.6, specs §8.2).
 *
 * Le §4.6 range dans cet onglet « la liste des routines, le lancement d'une
 * séance, la séance en direct, **l'historique des séances** ». Les trois
 * premiers existent depuis l'étape 5 et le début de celle-ci ; voici le
 * quatrième.
 *
 * 🔴 **Un sous-onglet, et pas une carte au bas des routines.** Les routines
 * répondent à « qu'est-ce que je fais aujourd'hui », l'historique à « qu'est-ce
 * que j'ai fait ». Ce sont deux gestes distincts, à deux moments distincts, et
 * mêler les deux ferait défiler un mois de séances avant d'atteindre le bouton
 * qu'on ouvre l'onglet pour presser.
 *
 * ⚠️ **Trois sous-onglets tiennent désormais**, grâce au correctif de l'étape 4
 * (`.n-segmented > * { flex: 0 0 auto }` + `overflow-x: auto`) : la rangée
 * déborde et **glisse** au lieu de comprimer ses libellés. Sans ce correctif,
 * ce troisième onglet aurait reproduit exactement le défaut latent que la V2
 * §2.6 avait mesuré.
 *
 * ⚠️ **Aucune recherche ici.** Les trois autres domaines en ont une parce
 * qu'on y cherche un objet par son nom ; une séance se repère par sa **date**,
 * et la liste est déjà bornée à trente par le serveur. Ajouter un champ de
 * recherche serait ajouter un contrôle pour un geste que personne ne fait.
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import type { WorkoutSessionDTO } from '../../../../shared/types';
import { readError } from '../../api/client';
import { SESSIONS_KEY, fetchSessions } from '../../api/sessions';
import { Card } from '../../components/nourri';
import { Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { formatClock } from '../../hooks/useSessionClock';

/**
 * Ce qu'une séance passée dit d'elle en une ligne.
 *
 * ⚠️ **Les séries `skipped` ne comptent pas dans le total fait**, mais la
 * séance ne s'en vante pas non plus : « 18 séries » est ce qui a été fait, et
 * c'est la seule grandeur qu'on relit. Compter les séries prévues donnerait un
 * nombre flatteur et faux.
 */
function summarize(session: WorkoutSessionDTO): string {
  const faites = session.sets.filter((set) => set.status === 'done').length;
  const parts = [`${String(faites)} série${faites > 1 ? 's' : ''}`];

  if (session.finishedAt !== null) {
    parts.push(formatClock(Math.floor((session.finishedAt - session.startedAt) / 1000)));
  }
  return parts.join(' · ');
}

export function SessionsSection() {
  const navigate = useNavigate();

  /*
   * ⚠️ **La même entrée de cache que celle qu'écrit l'écran de séance**
   * (`writeSession`) : revenir ici après avoir validé trois séries montre la
   * séance à jour, sans aller-retour. C'est ce que l'écriture dans le cache
   * achète — pas seulement la survie hors ligne.
   */
  const sessions = useQuery({ queryKey: SESSIONS_KEY, queryFn: fetchSessions });
  const liste = sessions.data ?? [];

  return (
    <div className="n-page">
      {sessions.isPending && <Hint>Chargement…</Hint>}
      <ReadNote error={readError(sessions)} quoi="les séances" />

      {!sessions.isPending && liste.length === 0 && readError(sessions) === null && (
        <p className="n-empty">
          Aucune séance pour l’instant. Ouvrez une routine et lancez-la : tout se déroule ici,
          même sans réseau.
        </p>
      )}

      {liste.length > 0 && (
        <Card padding="none">
          <ul className="n-list">
            {liste.map((session) => (
              <li key={session.clientId} className="n-list__row">
                <button
                  type="button"
                  className="n-list__main"
                  onClick={() => {
                    void navigate(`/musculation/seance/${session.clientId}`);
                  }}
                >
                  <span className="n-list__name">
                    {session.routineName ?? 'Séance libre'}
                    {/*
                      ⚠️ **Une séance en cours se signale par le mot**, jamais
                      par la seule couleur : `finishedAt === null` **est**
                      « en cours » (il n'y a pas de colonne `status`), et
                      c'est l'information qu'on cherche en ouvrant cet écran
                      après avoir fermé l'application au milieu.
                    */}
                    {session.finishedAt === null && ' · en cours'}
                  </span>
                  <span className="n-list__meta tnum">
                    {session.date} · {summarize(session)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {liste.length > 0 && (
        <Hint>
          Les trente séances les plus récentes. Les plus anciennes restent en base — elles
          alimentent le dernier passage de chaque exercice, aussi loin qu’il faut.
        </Hint>
      )}
    </div>
  );
}
